import React, { useCallback } from 'react'
import { Button, App, Space, message } from 'antd'
import { AudioOutlined, AudioMutedOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import useWebRTCConnection from '../hooks/useWebRTCConnection'
import { requestMicrophoneAccess } from '../utils/webrtc-utils'
import { processAIMessage } from '../utils/message-handlers'

/**
 * WebRTC-based AI Voice Assistant component
 *
 * @param {Object} props - Component props
 * @param {string} props.systemContext - System context for the AI (optional)
 * @param {Array} props.customTools - Custom tools for the AI (optional)
 * @param {Function} props.onAction - Callback for handling actions (optional)
 * @param {Object} props.contextVariables - Variables to inject into the context (optional)
 * @param {string} props.buttonText - Custom button text (optional)
 * @param {Object} props.buttonProps - Additional props for the button (optional)
 * @param {Object} props.containerStyle - Style for the container (optional)
 * @param {string} props.assistantName - Custom name for the assistant (optional)
 * @param {Array} props.actionTriggers - Custom action trigger phrases (optional)
 * @returns {JSX.Element}
 */
const WebRTCAssistant = ({
  systemContext,
  customTools = [],
  onAction,
  contextVariables = {},
  buttonText,
  buttonProps = {},
  containerStyle = {},
  assistantName = 'AI Assistant',
  actionTriggers = []
}) => {
  const { modal } = App.useApp()

  // Process the system context with variables
  const processedContext = useCallback(() => {
    if (!systemContext) {
      return getDefaultContext(assistantName)
    }

    // Replace variables in the context
    let context = systemContext
    Object.entries(contextVariables).forEach(([key, value]) => {
      context = context.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
    })

    return context
  }, [systemContext, contextVariables, assistantName])

  // Handle messages from the WebRTC connection
  const handleMessage = useCallback(
    (data) => {
      // Process message with custom action triggers
      processAIMessage(data, {
        onAction,
        setUserSpeaking: (value) => setUserSpeaking(value),
        setIsAISpeaking: (value) => setIsAISpeaking(value),
        actionTriggers
      })

      // Handle errors
      if (data.type === 'error') {
        console.error('Error from AI:', data.error)
        if (data.error?.message) {
          message.error(`AI Error: ${data.error.message}`)
        }
      }
    },
    [onAction, actionTriggers]
  )

  // Use the WebRTC connection hook
  const {
    isConnecting,
    isConnected,
    isListening,
    retryCount,
    isAISpeaking,
    userSpeaking,
    initWebRTC,
    cleanup,
    setRetryCount,
    setUserSpeaking,
    setIsAISpeaking,
    MAX_RETRIES
  } = useWebRTCConnection({
    systemContext: processedContext(),
    onMessage: handleMessage
  })

  // Handle connection button click
  const handleConnectionToggle = async () => {
    if (isConnected) {
      cleanup(true)
    } else {
      const stream = await requestMicrophoneAccess()
      if (stream) {
        setRetryCount(0)
        initWebRTC(stream)
      } else {
        modal.error({
          title: 'Microphone Access Required',
          content: 'Please enable microphone access to use the AI Assistant.'
        })
      }
    }
  }

  // Get button text based on connection state
  const getButtonText = () => {
    if (buttonText) return buttonText

    if (isConnecting) {
      return `Connecting (Attempt ${retryCount}/${MAX_RETRIES})...`
    } else if (isConnected) {
      return isListening ? `${assistantName} Active` : `${assistantName} Connected`
    } else {
      return `Start ${assistantName}`
    }
  }

  return (
    <Space direction='vertical' align='center' style={{ width: '100%', ...containerStyle }}>
      <div
        style={{
          padding: '10px',
          borderRadius: '8px',
          backgroundColor: '#f0f2f5',
          textAlign: 'center',
          marginBottom: '10px'
        }}
      >
        {userSpeaking && (
          <div style={{ color: '#1890ff' }}>
            <AudioOutlined style={{ fontSize: '24px' }} spin />
            <div>Listening...</div>
          </div>
        )}
        {isAISpeaking && (
          <div style={{ color: '#52c41a' }}>
            <AudioOutlined style={{ fontSize: '24px' }} spin />
            <div>{assistantName} Speaking...</div>
          </div>
        )}
        {!userSpeaking && !isAISpeaking && isConnected && (
          <div style={{ color: '#8c8c8c' }}>
            <AudioMutedOutlined style={{ fontSize: '24px' }} />
            <div>Ready</div>
          </div>
        )}
      </div>

      <Button
        type='primary'
        onClick={handleConnectionToggle}
        loading={isConnecting}
        icon={<AudioOutlined />}
        style={{ width: '100%' }}
        {...buttonProps}
      >
        {getButtonText()}
      </Button>
    </Space>
  )
}

/**
 * Get the default context for the AI assistant
 * @param {string} assistantName - Name of the assistant
 * @returns {string} Default context
 */
const getDefaultContext = (assistantName) => {
  return `You are a helpful ${assistantName}.

INITIAL GREETING:
Only say "Hello! How can I help you today?"

INTERACTION GUIDELINES:
- Keep responses focused and concise
- Be helpful and friendly
- Only explain features when asked "What can you do?"

Remember: Always wait for explicit questions before providing information.`
}

WebRTCAssistant.propTypes = {
  systemContext: PropTypes.string,
  customTools: PropTypes.arrayOf(PropTypes.object),
  onAction: PropTypes.func,
  contextVariables: PropTypes.object,
  buttonText: PropTypes.string,
  buttonProps: PropTypes.object,
  containerStyle: PropTypes.object,
  assistantName: PropTypes.string,
  actionTriggers: PropTypes.arrayOf(
    PropTypes.shape({
      phrases: PropTypes.arrayOf(PropTypes.string).isRequired,
      action: PropTypes.string.isRequired
    })
  )
}

export default WebRTCAssistant
